import { useState, useEffect, useContext, useCallback, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import {
  Flex,
  Text,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Box,
  IconButton,
  Tooltip,
  Spinner,
  Link,
  Checkbox,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useToast,
} from '@chakra-ui/react';

import { RepeatIcon, PhoneIcon } from '@chakra-ui/icons';

import Card from 'components/card/Card';
import Paginate from 'components/paginate/Paginate';
import SelectSearch from 'components/fields/SelectSearch';
import { SortableTh } from 'components/table';
import type { SortDirection } from 'components/table';

import {
  apiGetCallList,
  apiBulkAssignCallListItems,
  apiGetCallListUsers,
  apiGetCallListAnalytics,
} from 'api';

import type {
  APICallListData,
  APIPaginatedResponse,
  APIUserData,
  APICallListAnalyticsData,
} from 'types/api.types';

import ItemNote from 'views/call_list/components/ItemNote';
import ItemEffectiveDate from 'views/call_list/components/ItemEffectiveDate';
import ItemAssign from 'views/call_list/components/ItemAssign';
import ItemStatusChange from 'views/call_list/components/ItemStatusChange';
import ItemIgnore from 'views/call_list/components/ItemIgnore';

import { useUrlFilters } from 'hooks/useUrlFilters';

import { ROLE_ADMIN, ROLE_MANAGER } from 'variables/constants';
import { AuthContext } from 'contexts/AuthContext';

const TABLE_HEADERS: { label: string; field?: string }[] = [
  { label: '' },
  { label: 'Name', field: 'name' },
  { label: 'Effective Date', field: 'effective_date' },
  { label: 'Business Type', field: 'business_type' },
  { label: 'Lead Type' },
  { label: 'Phone' },
  { label: 'Action' },
  { label: '' },
];

const CALL_LIST_SORT_KEYS = ['sort_field', 'sort_direction'] as const;

const CallListView = () => {
  const { user } = useContext(AuthContext);
  const { getFilter, setFiltersAndPage, page, setPage } =
    useUrlFilters(CALL_LIST_SORT_KEYS);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [isAssigning, setIsAssigning] = useState(false);

  const toast = useToast();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const statLabelColor = useColorModeValue('gray.500', 'gray.400');
  const statValueColor = useColorModeValue('secondaryGray.900', 'white');

  const {
    data: analytics,
    isLoading: isLoadingAnalytics,
    isFetching: isFetchingAnalytics,
    refetch: refetchAnalytics,
  } = useQuery({
    queryKey: ['call-list-analytics'],
    queryFn: apiGetCallListAnalytics,
    refetchInterval: 60 * 1000, // Refetch every 1 minute
  }) as {
    data: APICallListAnalyticsData;
    isLoading: boolean;
    isFetching: boolean;
    refetch: () => void;
    error: any;
  };

  const hasAdminAccess =
    user?.role === ROLE_ADMIN || user?.role === ROLE_MANAGER;

  const sortField = getFilter('sort_field');
  const sortDirection = (
    getFilter('sort_direction') === 'desc' ? 'desc' : 'asc'
  ) as SortDirection;

  const sortParams = useMemo(
    () =>
      sortField ? { sort_field: sortField, sort_direction: sortDirection } : {},
    [sortField, sortDirection],
  );

  const handleSort = useCallback(
    (field: string) => {
      const nextDirection =
        sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
      setFiltersAndPage(
        { sort_field: field, sort_direction: nextDirection },
        0,
      );
    },
    [sortField, sortDirection, setFiltersAndPage],
  );

  const queryKey = useMemo(
    () => ['call-list', page, sortParams],
    [page, sortParams],
  );

  const { error, data, isLoading, isFetching, refetch } = useQuery({
    queryKey,
    queryFn: () => apiGetCallList(page + 1, sortParams),
  }) as {
    data: APIPaginatedResponse<APICallListData>;
    isLoading: boolean;
    error: any;
    isFetching: boolean;
    refetch: () => void;
  };

  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['call-list-users'],
    queryFn: apiGetCallListUsers,
    enabled: isModalOpen,
    staleTime: 5 * 60 * 1000, // 5 minutes
  }) as {
    data: APIUserData[];
    isLoading: boolean;
  };

  useEffect(() => {
    setSelectedItems([]);
  }, [page, data]);

  const handleAnalyticsRefetch = useCallback(() => {
    refetchAnalytics();
  }, [refetchAnalytics]);

  const handleSelectItem = useCallback((itemId: number) => {
    setSelectedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId],
    );
  }, []);

  const handleSelectAllItems = useCallback(() => {
    if (!data) return;

    if (selectedItems.length === data.results.length) {
      // If all items are selected, deselect all
      setSelectedItems([]);
    } else {
      // Select all items
      const allItemIds = data.results.map((item) => item.id);
      setSelectedItems(allItemIds);
    }
  }, [data, selectedItems]);

  const handleBulkAssign = async () => {
    if (!selectedUserId || selectedItems.length === 0) {
      toast({
        title: 'Error',
        description: 'Please select a user to assign the items to',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsAssigning(true);

    try {
      await apiBulkAssignCallListItems({
        items: selectedItems,
        user: selectedUserId,
      });

      toast({
        title: 'Success',
        description: `Successfully assigned ${selectedItems.length} items`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Reset state and refresh data
      setSelectedItems([]);
      setIsModalOpen(false);
      setSelectedUserId(null);
      setPage(0);
      refetch();
      refetchAnalytics();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to assign items',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      console.error('Bulk assignment error:', error);
    } finally {
      setIsAssigning(false);
    }
  };

  if (error) {
    return (
      <Card>
        <Box p="20px" pb="0">
          <Alert status="error">
            <AlertIcon />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{JSON.stringify(error)}</AlertDescription>
          </Alert>
        </Box>
      </Card>
    );
  }

  // Count items for select all functionality
  const itemsCount = data?.results.length || 0;
  const allItemsSelected =
    itemsCount > 0 && selectedItems.length === itemsCount;

  return (
    <>
      <Card mb="20px">
        <Flex gap={8} flexWrap="wrap">
          <Box
            borderRadius="md"
            p={4}
            minW="160px"
            boxShadow="sm"
            display="flex"
            flexDirection="column"
            alignItems="center"
            border="1px solid"
            borderColor={borderColor}
          >
            <Text fontSize="sm" color={statLabelColor} mb={1}>
              Calls Today
            </Text>
            <Box
              minH="36px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {isLoadingAnalytics || isFetchingAnalytics ? (
                <Spinner size="sm" color={statValueColor} />
              ) : (
                <Text fontSize="2xl" fontWeight="bold" color={statValueColor}>
                  {analytics.calls_today ?? '-'}
                </Text>
              )}
            </Box>
          </Box>
          <Box
            borderRadius="md"
            p={4}
            minW="160px"
            boxShadow="sm"
            display="flex"
            flexDirection="column"
            alignItems="center"
            border="1px solid"
            borderColor={borderColor}
          >
            <Text fontSize="sm" color={statLabelColor} mb={1}>
              Daily Call Average
            </Text>
            <Box
              minH="32px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {isLoadingAnalytics || isFetchingAnalytics ? (
                <Spinner size="sm" color={statValueColor} />
              ) : (
                <Text fontSize="2xl" fontWeight="bold" color={statValueColor}>
                  {analytics.daily_call_average ?? '-'}
                </Text>
              )}
            </Box>
          </Box>
          <Box
            borderRadius="md"
            p={4}
            minW="160px"
            boxShadow="sm"
            display="flex"
            flexDirection="column"
            alignItems="center"
            border="1px solid"
            borderColor={borderColor}
          >
            <Text fontSize="sm" color={statLabelColor} mb={1}>
              Connection Ratio
            </Text>
            <Box
              minH="32px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {isLoadingAnalytics || isFetchingAnalytics ? (
                <Spinner size="sm" color={statValueColor} />
              ) : (
                <Text fontSize="2xl" fontWeight="bold" color={statValueColor}>
                  {analytics.connection_ratio}%
                </Text>
              )}
            </Box>
          </Box>
          <Box
            borderRadius="md"
            p={4}
            minW="160px"
            boxShadow="sm"
            display="flex"
            flexDirection="column"
            alignItems="center"
            border="1px solid"
            borderColor={borderColor}
          >
            <Text fontSize="sm" color={statLabelColor} mb={1}>
              Call to Quote Ratio
            </Text>
            <Box
              minH="32px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {isLoadingAnalytics || isFetchingAnalytics ? (
                <Spinner size="sm" color={statValueColor} />
              ) : (
                <Text fontSize="2xl" fontWeight="bold" color={statValueColor}>
                  {analytics.call_to_quote_ratio}%
                </Text>
              )}
            </Box>
          </Box>
          <Box
            borderRadius="md"
            p={4}
            minW="160px"
            boxShadow="sm"
            display="flex"
            flexDirection="column"
            alignItems="center"
            border="1px solid"
            borderColor={borderColor}
          >
            <Text fontSize="sm" color={statLabelColor} mb={1}>
              Ignored Lead Ratio
            </Text>
            <Box
              minH="32px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {isLoadingAnalytics || isFetchingAnalytics ? (
                <Spinner size="sm" color={statValueColor} />
              ) : (
                <Text fontSize="2xl" fontWeight="bold" color={statValueColor}>
                  {analytics.ignored_lead_ratio}%
                </Text>
              )}
            </Box>
          </Box>
        </Flex>
      </Card>

      <Card
        flexDirection="column"
        w="100%"
        px="0px"
        overflowX={{ sm: 'scroll', lg: 'hidden' }}
        mb="20px"
      >
        <Flex px="20px" justifyContent="space-between" align="center" mb="20px">
          <Text
            color={textColor}
            fontSize="22px"
            fontWeight="700"
            lineHeight="100%"
          >
            Call List
          </Text>
          <Flex gap={2}>
            <Tooltip label="Refresh call list" hasArrow>
              <IconButton
                aria-label="Refresh call list"
                icon={<RepeatIcon />}
                onClick={() => {
                  refetch();
                  handleAnalyticsRefetch();
                }}
                variant="ghost"
                isLoading={isLoading || isFetching}
              />
            </Tooltip>
            {hasAdminAccess && selectedItems.length > 0 && (
              <Button variant="brand" onClick={() => setIsModalOpen(true)}>
                Assign {selectedItems.length} Item
                {selectedItems.length !== 1 ? 's' : ''}
              </Button>
            )}
          </Flex>
        </Flex>

        <Table variant="simple" color="gray.500">
          <Thead>
            <Tr>
              {TABLE_HEADERS.map(({ label, field }, index) => {
                if (index === 0 && !hasAdminAccess) return null;

                if (field) {
                  return (
                    <SortableTh
                      key={label}
                      label={label}
                      field={field}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                      borderColor={borderColor}
                    />
                  );
                }

                return (
                  <Th
                    key={label || `header-${index}`}
                    pe="10px"
                    borderColor={borderColor}
                    color="gray.500"
                  >
                    {index === 0 && hasAdminAccess && itemsCount > 0 ? (
                      <Checkbox
                        isChecked={allItemsSelected}
                        onChange={handleSelectAllItems}
                        colorScheme="blue"
                        borderColor="gray.600"
                      />
                    ) : (
                      label
                    )}
                  </Th>
                );
              })}
            </Tr>
          </Thead>
          <Tbody>
            {isLoading && (
              <Tr>
                <Td colSpan={TABLE_HEADERS.length} textAlign="center">
                  <Flex align="center" justify="center" mt="20px">
                    <Spinner size="sm" mr="10px" /> Loading call list...
                  </Flex>
                </Td>
              </Tr>
            )}

            {data?.results.map((call) => (
              <Tr
                key={call.id}
                _hover={{
                  bg: 'gray.200',
                }}
              >
                {hasAdminAccess && (
                  <Td>
                    <Checkbox
                      isChecked={selectedItems.includes(call.id)}
                      onChange={() => handleSelectItem(call.id)}
                      colorScheme="blue"
                      borderColor="gray.600"
                    />
                  </Td>
                )}

                <Td>
                  <Flex align="center">
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {call.name}
                      {call.user && <ItemNote item={call} />}
                    </Text>
                  </Flex>
                </Td>
                <Td>
                  <Flex align="center">
                    <Text color={textColor} fontSize="sm">
                      {call.user ? (
                        <ItemEffectiveDate item={call} onAction={refetch} />
                      ) : (
                        new Date(call.effective_date).toLocaleDateString()
                      )}
                    </Text>
                  </Flex>
                </Td>
                <Td>
                  <Flex align="center">
                    <Text color={textColor} fontSize="sm">
                      {call.busines_type_display}
                    </Text>
                  </Flex>
                </Td>
                <Td>
                  <Flex align="center">
                    <Text color={textColor} fontSize="sm">
                      {call.lead_type_display}
                    </Text>
                  </Flex>
                </Td>
                <Td>
                  <Flex align="center">
                    {call.user ? (
                      <Tooltip
                        label="Call with RingCentral"
                        placement="top"
                        hasArrow
                      >
                        <Link
                          href={`rcapp://r/dialer?number=${call.phone_number}`}
                          width="100%"
                          textAlign="center"
                          _hover={{
                            color: 'green.400',
                            backgroundColor: 'gray.200',
                          }}
                          borderRadius="16px"
                          py={2}
                        >
                          <PhoneIcon />
                        </Link>
                      </Tooltip>
                    ) : (
                      '-'
                    )}
                  </Flex>
                </Td>
                <Td>
                  <Flex align="center">
                    {!call.user && (
                      <ItemAssign
                        item={call}
                        queryKey={queryKey}
                        onAction={handleAnalyticsRefetch}
                      />
                    )}
                    {call.user && (
                      <ItemStatusChange
                        item={call}
                        queryKey={queryKey}
                        onAction={handleAnalyticsRefetch}
                      />
                    )}
                  </Flex>
                </Td>
                <Td>
                  <ItemIgnore
                    item={call}
                    queryKey={queryKey}
                    onAction={handleAnalyticsRefetch}
                  />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>

        {data && data.results.length === 0 && (
          <Box textAlign="center" w="100%" mt="20px">
            No calls found
          </Box>
        )}

        <Paginate page={page} count={data?.count || 0} onSetPage={setPage} />

        {hasAdminAccess && (
          <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Assign Items to User</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Text mb={3}>
                  Select a user to assign {selectedItems.length} item
                  {selectedItems.length !== 1 ? 's' : ''} to:
                </Text>
                <SelectSearch
                  options={
                    usersData?.map((user) => ({
                      value: user.id.toString(),
                      label: `${user.first_name} ${user.last_name}`,
                    })) || []
                  }
                  value={selectedUserId?.toString() || ''}
                  onChange={(value) =>
                    setSelectedUserId(value ? Number(value) : null)
                  }
                  placeholder="Select a user"
                  isLoading={isLoadingUsers}
                />
              </ModalBody>

              <ModalFooter>
                <Button variant="ghost" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  mr={3}
                  onClick={handleBulkAssign}
                  isLoading={isAssigning}
                  isDisabled={!selectedUserId || isLoadingUsers}
                >
                  Confirm
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        )}
      </Card>
    </>
  );
};

export default CallListView;
